import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import studentService from "../../services/studentService";
import courseService from "../../services/courseService";
import { getYearOptions } from "../../utils/courseYears";

const MAX_PHOTO_SIZE = 2 * 1024 * 1024;

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan",
  "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli", "Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const ADMISSION_TYPES = ["CAP-1", "CAP-2", "CAP-3", "CAP-4", "Institute Level"];
const ADMISSION_CATEGORIES = ["Open", "OBC", "SC", "ST", "NT-A", "NT-B", "NT-C", "NT-D", "VJ", "SBC", "TFWS", "Open Minority Scholarship", "EWS"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const STATUSES = ["Active", "Passed", "Detained", "Dropped", "Passed Out"];

const TABS = [
  { id: "identity", label: "1. Identity" },
  { id: "academic", label: "2. Academic" },
  { id: "family", label: "3. Family" },
  { id: "birth", label: "4. Birth Details" },
  { id: "address", label: "5. Address" },
  { id: "bank", label: "6. Bank & Other" },
];

const EMPTY_FORM = {
  // Identity
  institueId: "", firstName: "", middleName: "", lastName: "", fullName: "",
  bloodGroup: "", gender: "", maritalStatus: "", dob: "",
  aadharNo: "", aaparId: "", nationality: "Indian",
  studentPhotoUrl: "", studentPhotoPublicId: "", studentPhotoDataUrl: "",
  // Academic
  courseAdmitted: "", courseYear: "", enrollmentNo: "",
  course: "", yearNumber: "",
  admissionType: "", admissionCategory: "", generalRegNo: "", rollNo: "",
  instituteAdmissionDate: "", currentAdmissionDate: "",
  previousPassingYear: "", divisionName: "", feeStructureName: "",
  studentType: "", firstYearStream: "", previousSchoolName: "",
  feesDecided: "", scholarshipAmountBalance: "", status: "Active",
  // Family
  fatherName: "", motherName: "",
  fatherMobile: "", motherMobile: "", telephoneNo: "",
  fatherDob: "", motherDob: "",
  fatherEduction: "", motherEducation: "",
  parentOccupation: "", motherOccupation: "",
  parentIncome: "", motherIncome: "",
  parentAddress: "", parentEmailAddress: "",
  // Religion/Caste
  religion: "", caste: "", subCaste: "", motherTongue: "",
  isBplCardHolder: "", bplCardNo: "", rationCardNo: "",
  // Birth
  placeOfBirth: "", birthDistrict: "", birthTaluka: "",
  birthState: "", birthCountry: "India", birthUrbanRural: "",
  // Address
  mobile: "", email: "",
  address: "", city: "", taluka: "", addressUrbanRural: "",
  district: "", state: "", pincode: "", country: "India",
  // Bank
  bankAccountNo: "", bankName: "", branchName: "", ifscCode: "",
};

const StudentFormContext = createContext(null);

// ─────────────────────────────────────────────────────────────────────────────
// Tab component
// ─────────────────────────────────────────────────────────────────────────────
function TabBar({ active, onChange, tabErrors }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "1.25rem", borderBottom: "2px solid var(--border)", paddingBottom: "0" }}>
      {TABS.map((t) => {
        const hasErr = tabErrors.includes(t.id);
        const isActive = active === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            style={{
              padding: "0.55rem 1rem",
              border: "none",
              borderBottom: isActive ? "3px solid #003b67" : "3px solid transparent",
              background: "transparent",
              fontSize: "14px",
              fontWeight: isActive ? 600 : 400,
              color: hasErr ? "#dc2626" : isActive ? "#003b67" : "var(--text-muted)",
              cursor: "pointer",
              marginBottom: "-2px",
              borderRadius: "4px 4px 0 0",
              transition: "all 0.15s",
              position: "relative",
              textTransform: "uppercase"
            }}
          >
            {t.label}
            {hasErr && (
              <span style={{ position: "absolute", top: 4, right: 4, width: 7, height: 7, background: "#dc2626", borderRadius: "50%" }} />
            )}
          </button>
        );
      })}
    </div>
  );
}

function StudentField({
  label,
  name,
  placeholder,
  hint,
  type = "text",
  required,
  half,
  value,
  onChange,
  error,
}) {
  const context = useContext(StudentFormContext);
  const resolvedValue = value ?? context?.form?.[name] ?? "";
  const resolvedOnChange = onChange ?? context?.handleChange;
  const resolvedError = error ?? context?.errors?.[name];

  return (
    <div className="form-group" style={half ? { gridColumn: "span 1" } : {}}>
      <label className="form-label">
        {label}
        {required && <span style={{ color: "var(--danger)" }}> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={resolvedValue}
        onChange={resolvedOnChange}
        placeholder={placeholder}
        className={`form-control ${resolvedError ? "error" : ""}`}
      />
      {hint && <span className="form-hint">{hint}</span>}
      {resolvedError && <span className="form-error">{resolvedError}</span>}
    </div>
  );
}

function StudentSelectField({
  label,
  name,
  options,
  required,
  placeholder = "Select...",
  value,
  onChange,
  error,
}) {
  const context = useContext(StudentFormContext);
  const resolvedValue = value ?? context?.form?.[name] ?? "";
  const resolvedOnChange = onChange ?? context?.handleChange;
  const resolvedError = error ?? context?.errors?.[name];

  return (
    <div className="form-group">
      <label className="form-label">
        {label}
        {required && <span style={{ color: "var(--danger)" }}> *</span>}
      </label>
      <select
        name={name}
        value={resolvedValue}
        onChange={resolvedOnChange}
        className={`form-control ${resolvedError ? "error" : ""}`}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.v || o} value={o.v || o}>
            {o.l || o}
          </option>
        ))}
      </select>
      {resolvedError && <span className="form-error">{resolvedError}</span>}
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div
      style={{
        gridColumn: "1 / -1",
        fontSize: 14,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: "var(--text-muted)",
        borderBottom: "1px solid var(--border)",
        paddingBottom: "4px",
        marginTop: "0.5rem",
      }}
    >
      {title}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Field component
// ─────────────────────────────────────────────────────────────────────────────
export default function StudentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { academicYear } = useApp();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [activeTab, setActiveTab] = useState("identity");
  const [removeStudentPhoto, setRemoveStudentPhoto] = useState(false);

  const [courses, setCourses] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await courseService.getAll({ isActive: true });
        setCourses(res.data || []);
      } catch (err) {
        toast.error("Couldn't load courses: " + err.message);
      }
    })();
  }, []);

  // Auto-build fullName from parts
  useEffect(() => {
    if (!isEdit || !form.firstName) {
      const full = [form.firstName, form.middleName, form.lastName].filter(Boolean).join(" ");
      if (full) setForm((p) => ({ ...p, fullName: full }));
    }
  }, [form.firstName, form.middleName, form.lastName, isEdit]);

  // Load existing student in edit mode
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await studentService.getById(id);
        const s = res.data;
        setRemoveStudentPhoto(false);
        setForm({
          institueId: s.institueId != null ? String(s.institueId) : "",
          firstName: s.firstName || "",
          middleName: s.middleName || "",
          lastName: s.lastName || "",
          fullName: s.fullName || "",
          bloodGroup: s.bloodGroup || "",
          gender: s.gender || "",
          maritalStatus: s.maritalStatus || "",
          dob: s.dob || "",
          aadharNo: s.aadharNo || "",
          aaparId: s.aaparId || "",
          nationality: s.nationality || "Indian",
          studentPhotoUrl: s.studentPhoto?.url || "",
          studentPhotoPublicId: s.studentPhoto?.publicId || "",
          course: s.course?._id || s.course || "",
          yearNumber: s.yearNumber != null ? String(s.yearNumber) : "",
          enrollmentNo: s.enrollmentNo || "",
          admissionType: s.admissionType || "",
          admissionCategory: s.admissionCategory || "",
          generalRegNo: s.generalRegNo != null ? String(s.generalRegNo) : "",
          rollNo: s.rollNo != null ? String(s.rollNo) : "",
          instituteAdmissionDate: s.instituteAdmissionDate || "",
          currentAdmissionDate: s.currentAdmissionDate || "",
          previousPassingYear: s.previousPassingYear || "",
          divisionName: s.divisionName || "",
          feeStructureName: s.feeStructureName || "",
          studentType: s.studentType || "",
          firstYearStream: s.firstYearStream || "",
          previousSchoolName: s.previousSchoolName || "",
          feesDecided: s.feesDecided != null ? String(s.feesDecided) : "",
          scholarshipAmountBalance: s.scholarshipAmountBalance != null ? String(s.scholarshipAmountBalance) : "",
          status: s.status || "Active",
          fatherName: s.fatherName || "",
          motherName: s.motherName || "",
          fatherMobile: s.fatherMobile || "",
          motherMobile: s.motherMobile || "",
          telephoneNo: s.telephoneNo || "",
          fatherDob: s.fatherDob || "",
          motherDob: s.motherDob || "",
          fatherEduction: s.fatherEduction || "",
          motherEducation: s.motherEducation || "",
          parentOccupation: s.parentOccupation || "",
          motherOccupation: s.motherOccupation || "",
          parentIncome: s.parentIncome != null ? String(s.parentIncome) : "",
          motherIncome: s.motherIncome != null ? String(s.motherIncome) : "",
          parentAddress: s.parentAddress || "",
          parentEmailAddress: s.parentEmailAddress || "",
          religion: s.religion || "",
          caste: s.caste || "",
          subCaste: s.subCaste || "",
          motherTongue: s.motherTongue || "",
          isBplCardHolder: s.isBplCardHolder != null ? String(s.isBplCardHolder) : "",
          bplCardNo: s.bplCardNo || "",
          rationCardNo: s.rationCardNo || "",
          placeOfBirth: s.placeOfBirth || "",
          birthDistrict: s.birthDistrict || "",
          birthTaluka: s.birthTaluka || "",
          birthState: s.birthState || "",
          birthCountry: s.birthCountry || "India",
          birthUrbanRural: s.birthUrbanRural || "",
          mobile: s.mobile || "",
          email: s.email || "",
          address: s.address || "",
          city: s.city || "",
          taluka: s.taluka || "",
          addressUrbanRural: s.addressUrbanRural || "",
          district: s.district || "",
          state: s.state || "",
          pincode: s.pincode || "",
          country: s.country || "India",
          bankAccountNo: s.bankAccountNo || "",
          bankName: s.bankName || "",
          branchName: s.branchName || "",
          ifscCode: s.ifscCode || "",
        });
      } catch (err) {
        toast.error(err.message);
        navigate("/students");
      } finally {
        setFetching(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read image file."));
      reader.readAsDataURL(file);
    });

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please choose a valid image file.");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_PHOTO_SIZE) {
      toast.error("Photo must be 2 MB or smaller.");
      e.target.value = "";
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setForm((prev) => ({
        ...prev,
        studentPhotoUrl: dataUrl,
        studentPhotoDataUrl: dataUrl,
      }));
      setRemoveStudentPhoto(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      e.target.value = "";
    }
  };

  const clearPhoto = () => {
    setForm((prev) => ({
      ...prev,
      studentPhotoUrl: "",
      studentPhotoPublicId: "",
      studentPhotoDataUrl: "",
    }));
    setRemoveStudentPhoto(Boolean(isEdit));
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    // Identity
    if (!form.firstName.trim()) errs.firstName = "Required";
    if (!form.middleName.trim()) errs.middleName = "Required";
    if (!form.lastName.trim()) errs.lastName = "Required";
    if (!form.fullName.trim()) errs.fullName = "Required";
    if (!form.gender) errs.gender = "Required";
    if (!form.dob.trim()) errs.dob = "Required";
    if (form.dob && !/^\d{2}\/\d{2}\/\d{4}$/.test(form.dob)) errs.dob = "Format: DD/MM/YYYY";
    if (!form.aadharNo.trim()) errs.aadharNo = "Required";
    if (form.aadharNo && !/^\d{12}$/.test(form.aadharNo)) errs.aadharNo = "Must be 12 digits";

    // Academic
    // if (!form.courseAdmitted.trim()) errs.courseAdmitted = "Required";
    // if (!form.courseYear.trim()) errs.courseYear = "Required";
    // Academic
    if (!form.course) errs.course = "Required";
    if (!form.yearNumber) errs.yearNumber = "Required";
    if (!form.enrollmentNo.trim()) errs.enrollmentNo = "Required";
    if (!form.admissionType) errs.admissionType = "Required";
    if (!form.admissionCategory) errs.admissionCategory = "Required";
    if (!form.instituteAdmissionDate.trim()) errs.instituteAdmissionDate = "Required";
    if (form.instituteAdmissionDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(form.instituteAdmissionDate)) errs.instituteAdmissionDate = "Format: DD/MM/YYYY";
    if (!form.currentAdmissionDate.trim()) errs.currentAdmissionDate = "Required";
    if (form.currentAdmissionDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(form.currentAdmissionDate)) errs.currentAdmissionDate = "Format: DD/MM/YYYY";
    if (!form.previousSchoolName.trim()) errs.previousSchoolName = "Required";

    // Family
    if (!form.fatherName.trim()) errs.fatherName = "Required";
    if (!form.motherName.trim()) errs.motherName = "Required";
    if (form.fatherMobile && !/^\d{10}$/.test(form.fatherMobile)) errs.fatherMobile = "Must be 10 digits";
    if (form.motherMobile && !/^\d{10}$/.test(form.motherMobile)) errs.motherMobile = "Must be 10 digits";
    if (form.fatherDob && !/^\d{2}\/\d{2}\/\d{4}$/.test(form.fatherDob)) errs.fatherDob = "Format: DD/MM/YYYY";
    if (form.motherDob && !/^\d{2}\/\d{2}\/\d{4}$/.test(form.motherDob)) errs.motherDob = "Format: DD/MM/YYYY";

    // Religion/Caste (in identity tab)
    if (!form.religion.trim()) errs.religion = "Required";
    if (!form.caste.trim()) errs.caste = "Required";
    if (!form.motherTongue.trim()) errs.motherTongue = "Required";

    // Birth
    if (!form.placeOfBirth.trim()) errs.placeOfBirth = "Required";
    if (!form.birthState) errs.birthState = "Required";

    // Address
    if (!form.mobile.trim()) errs.mobile = "Required";
    if (form.mobile && !/^\d{10}$/.test(form.mobile)) errs.mobile = "Must be 10 digits";
    if (!form.address.trim()) errs.address = "Required";
    if (!form.city.trim()) errs.city = "Required";
    if (!form.state) errs.state = "Required";
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) errs.pincode = "Must be 6 digits";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Invalid email";

    return errs;
  };

  // Which tab has errors?
  const tabErrorMap = {
    identity: ["firstName", "middleName", "lastName", "fullName", "gender", "dob", "aadharNo", "religion", "caste", "motherTongue"],
    academic: ["course", "yearNumber", "enrollmentNo", "admissionType", "admissionCategory", "instituteAdmissionDate", "currentAdmissionDate", "previousSchoolName"],
    family: ["fatherName", "motherName", "fatherMobile", "motherMobile", "fatherDob", "motherDob"],
    birth: ["placeOfBirth", "birthState"],
    address: ["mobile", "address", "city", "state", "pincode", "email"],
    bank: [],
  };

  const getTabsWithErrors = (errs) =>
    Object.keys(tabErrorMap).filter((tab) =>
      tabErrorMap[tab].some((f) => errs[f])
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();

    if (Object.keys(errs).length) {
      setErrors(errs);
      const tabs = getTabsWithErrors(errs);
      if (tabs.length) setActiveTab(tabs[0]);
      toast.error("Please fix the errors before saving.");
      return;
    }

    if (!isEdit && !academicYear?._id) {
      toast.error("Please select an Academic Year from the Dashboard first.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        institueId: form.institueId !== "" ? Number(form.institueId) : undefined,
        generalRegNo: form.generalRegNo !== "" ? Number(form.generalRegNo) : undefined,
        rollNo: form.rollNo !== "" ? Number(form.rollNo) : undefined,
        yearNumber: form.yearNumber !== "" ? Number(form.yearNumber) : undefined,
        feesDecided: form.feesDecided !== "" ? Number(form.feesDecided) : 0,
        scholarshipAmountBalance: form.scholarshipAmountBalance !== "" ? Number(form.scholarshipAmountBalance) : 0,
        parentIncome: form.parentIncome !== "" ? Number(form.parentIncome) : undefined,
        motherIncome: form.motherIncome !== "" ? Number(form.motherIncome) : undefined,
        isBplCardHolder: form.isBplCardHolder === "true" ? true : form.isBplCardHolder === "false" ? false : undefined,
        removeStudentPhoto,
        ...(!isEdit && { academicYear: academicYear._id }),
      };

      if (isEdit) {
        await studentService.update(id, payload);
        toast.success("Student updated successfully");
      } else {
        await studentService.create(payload);
        toast.success("Student added successfully");
      }
      navigate("/students");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Field helpers ───────────────────────────────────────────────────────────
  const F = StudentField;
  const S = StudentSelectField;
  const SH = SectionHeader;
  const selectedCourse = courses.find((c) => c._id === form.course);
  const yearOptions = selectedCourse ? getYearOptions(selectedCourse) : [];

  if (fetching) return <div className="loading-center"><div className="spinner" />Loading...</div>;

  const tabErrors = getTabsWithErrors(errors);
  const currentPhoto = form.studentPhotoUrl;

  return (
    <StudentFormContext.Provider value={{ form, errors, handleChange }}>
      <div style={{ maxWidth: 950 }}>
        <div className="page-header">
          <div>
            <h1 className="page-title">{isEdit ? "Edit Student" : "Add New Student"}</h1>
            <p className="page-subtitle">
              {!isEdit && academicYear ? `Academic Year: ${academicYear.label}` : isEdit ? "Update student details" : ""}
            </p>
          </div>
          <button className="btn btn-outline" onClick={() => navigate("/students")}>← Back</button>
        </div>

        {!isEdit && !academicYear && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#dc2626" }}>
            ⚠️ No academic year selected. Go to Dashboard and select a year first.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="card">
            <div className="card-body">
              <TabBar active={activeTab} onChange={setActiveTab} tabErrors={tabErrors} />

              {/* ════════════════════════════════════════════════════════════
                TAB 1 — IDENTITY
            ════════════════════════════════════════════════════════════ */}
              {activeTab === "identity" && (
                <div className="form-grid">
                  <SH title="Name" />
                  <F label="First Name" name="firstName" placeholder="e.g. Ameena" required />
                  <F label="Middle Name" name="middleName" placeholder="e.g. Amber" required />
                  <F label="Last Name" name="lastName" placeholder="e.g. Shahid" required />
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Full Name <span style={{ color: "var(--danger)" }}>*</span></label>
                    <input name="fullName" value={form.fullName} onChange={handleChange}
                      placeholder="Auto-filled from above — edit if needed"
                      className={`form-control ${errors.fullName ? "error" : ""}`} />
                    <span className="form-hint">Auto-built from First + Middle + Last — adjust if needed</span>
                    {errors.fullName && <span className="form-error">{errors.fullName}</span>}
                  </div>

                  <SH title="Personal" />
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Student Photo</label>
                    <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{
                        width: 110,
                        height: 130,
                        borderRadius: 12,
                        overflow: "hidden",
                        border: "1px solid var(--border)",
                        background: "#f8fafc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--text-muted)",
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        {currentPhoto ? (
                          <img
                            src={currentPhoto}
                            alt="Student"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <span>No Photo</span>
                        )}
                      </div>
                      <div style={{ display: "grid", gap: "0.55rem" }}>
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={handlePhotoChange}
                          style={{ maxWidth: 320 }}
                        />
                        <span className="form-hint">
                          JPG, PNG or WEBP up to 2 MB. Saved to Cloudinary when you submit.
                        </span>
                        {currentPhoto && (
                          <div>
                            <button type="button" className="btn btn-outline btn-sm" onClick={clearPhoto}>
                              Remove Photo
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <S label="Gender" name="gender" required options={["Male", "Female", "Other"]} />
                  <S label="Blood Group" name="bloodGroup" options={BLOOD_GROUPS} />
                  <F label="Date of Birth" name="dob" placeholder="DD/MM/YYYY" required hint="e.g. 18/07/2006" />
                  <S label="Marital Status" name="maritalStatus" options={["Married", "Unmarried"]} />
                  <F label="Aadhaar Number" name="aadharNo" placeholder="12-digit Aadhaar" required hint="Exactly 12 digits" />
                  <F label="APAAR ID" name="aaparId" placeholder="Optional APAAR ID" hint="Automated Permanent Academic Account Registry" />
                  <F label="Nationality" name="nationality" placeholder="Indian" />

                  <SH title="Religion / Caste" />
                  <F label="Mother Tongue" name="motherTongue" placeholder="e.g. Urdu" required />
                  <F label="Religion" name="religion" placeholder="e.g. Islam" required />
                  <F label="Caste" name="caste" placeholder="e.g. Muslim" required />
                  <F label="Sub Caste" name="subCaste" placeholder="(if none)" />

                  {isEdit && (
                    <>
                      <SH title="Status" />
                      <S label="Student Status" name="status" options={STATUSES} />
                    </>
                  )}
                </div>
              )}

              {/* ════════════════════════════════════════════════════════════
                TAB 2 — ACADEMIC & ADMISSION
            ════════════════════════════════════════════════════════════ */}
              {activeTab === "academic" && (
                <div className="form-grid">
                  <SH title="Course" />
                  <S
                    label="Course"
                    name="course"
                    required
                    options={courses.map((c) => ({ v: c._id, l: c.shortName }))}
                    value={form.course}
                    onChange={(e) => {
                      handleChange(e);
                      setForm((p) => ({ ...p, yearNumber: "" })); // year options depend on the course
                    }}
                    error={errors.course}
                  />
                  <S
                    label="Course Year"
                    name="yearNumber"
                    required
                    options={yearOptions.map((y) => ({ v: y.yearNumber, l: y.label }))}
                    placeholder={form.course ? "Select..." : "Select a course first"}
                    value={form.yearNumber}
                    error={errors.yearNumber}
                  />
                  <F label="Enrollment No." name="enrollmentNo" placeholder="e.g. 2146511823056" required />
                  <F label="General Reg. No." name="generalRegNo" placeholder="Number" type="number" />
                  <F label="Roll No." name="rollNo" placeholder="Number" type="number" />

                  <SH title="Admission" />
                  <S label="Admission Type" name="admissionType" required options={ADMISSION_TYPES} />
                  <S label="Admission Category" name="admissionCategory" required options={ADMISSION_CATEGORIES} />
                  <S label="Student Type" name="studentType" options={["New Student", "Old Student"]} />
                  <F label="Institute Admission Date" name="instituteAdmissionDate" placeholder="DD/MM/YYYY" required hint="Date of first admission to institute" />
                  <F label="Current Admission Date" name="currentAdmissionDate" placeholder="DD/MM/YYYY" required hint="Admission date for current year" />
                  <F label="Previous Passing Year" name="previousPassingYear" placeholder="e.g. 2024" />
                  <F label="Division Name" name="divisionName" placeholder="e.g. A" />
                  <F label="Fee Structure Name" name="feeStructureName" placeholder="e.g. Regular" />
                  <F label="First Year Stream" name="firstYearStream" placeholder="e.g. Science" />

                  <SH title="Previous Education" />
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Previous School / College <span style={{ color: "var(--danger)" }}>*</span></label>
                    <input name="previousSchoolName" value={form.previousSchoolName} onChange={handleChange}
                      placeholder="e.g. Rahbar Jr. College of Science, Akola"
                      className={`form-control ${errors.previousSchoolName ? "error" : ""}`} />
                    {errors.previousSchoolName && <span className="form-error">{errors.previousSchoolName}</span>}
                  </div>

                  <SH title="Fees" />
                  <div className="form-group">
                    <label className="form-label">Fees Decided (₹)</label>
                    <input type="number" name="feesDecided" value={form.feesDecided} onChange={handleChange} min="0" className="form-control" placeholder="e.g. 75000" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Scholarship Amount Balance (₹)</label>
                    <input type="number" name="scholarshipAmountBalance" value={form.scholarshipAmountBalance} onChange={handleChange} min="0" className="form-control" placeholder="e.g. 25000" />
                  </div>
                  <F label="Institute ID (optional)" name="institueId" placeholder="Auto-assigned number" type="number" />
                </div>
              )}

              {/* ════════════════════════════════════════════════════════════
                TAB 3 — FAMILY DETAILS
            ════════════════════════════════════════════════════════════ */}
              {activeTab === "family" && (
                <div className="form-grid">
                  <SH title="Father's Details" />
                  <F label="Father's Name" name="fatherName" placeholder="e.g. Mohd Shahid" required />
                  <F label="Father's Mobile" name="fatherMobile" placeholder="10-digit mobile" hint="Optional" />
                  <F label="Father's Date of Birth" name="fatherDob" placeholder="DD/MM/YYYY" />
                  <F label="Father's Education" name="fatherEduction" placeholder="e.g. Graduate" />
                  <F label="Father's Occupation" name="parentOccupation" placeholder="e.g. Farmer" />
                  <div className="form-group">
                    <label className="form-label">Father's Annual Income (₹)</label>
                    <input type="number" name="parentIncome" value={form.parentIncome} onChange={handleChange} min="0" className="form-control" placeholder="e.g. 200000" />
                  </div>

                  <SH title="Mother's Details" />
                  <F label="Mother's Name" name="motherName" placeholder="e.g. Amber Bi" required />
                  <F label="Mother's Mobile" name="motherMobile" placeholder="10-digit mobile" hint="Optional" />
                  <F label="Mother's Date of Birth" name="motherDob" placeholder="DD/MM/YYYY" />
                  <F label="Mother's Education" name="motherEducation" placeholder="e.g. Graduate" />
                  <F label="Mother's Occupation" name="motherOccupation" placeholder="e.g. Homemaker" />
                  <div className="form-group">
                    <label className="form-label">Mother's Annual Income (₹)</label>
                    <input type="number" name="motherIncome" value={form.motherIncome} onChange={handleChange} min="0" className="form-control" placeholder="e.g. 0" />
                  </div>

                  <SH title="Parent Contact" />
                  <F label="Telephone No." name="telephoneNo" placeholder="Landline number" />
                  <F label="Parent Email" name="parentEmailAddress" placeholder="parent@example.com" type="email" />
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Parent Address (if different)</label>
                    <textarea name="parentAddress" value={form.parentAddress} onChange={handleChange}
                      rows={2} placeholder="Leave blank if same as student address"
                      className="form-control" style={{ resize: "vertical" }} />
                  </div>

                  <SH title="BPL / Ration" />
                  <S label="BPL Card Holder?" name="isBplCardHolder" options={[{ v: "true", l: "Yes" }, { v: "false", l: "No" }]} />
                  <F label="BPL Card No." name="bplCardNo" placeholder="(if applicable)" />
                  <F label="Ration Card No." name="rationCardNo" placeholder="(if applicable)" />
                </div>
              )}

              {/* ════════════════════════════════════════════════════════════
                TAB 4 — BIRTH DETAILS
            ════════════════════════════════════════════════════════════ */}
              {activeTab === "birth" && (
                <div className="form-grid">
                  <SH title="Place of Birth" />
                  <F label="Place of Birth" name="placeOfBirth" placeholder="e.g. Nagpur" required />
                  <F label="Birth District" name="birthDistrict" placeholder="e.g. Nagpur" />
                  <F label="Birth Taluka" name="birthTaluka" placeholder="e.g. Nagpur" />
                  <S label="Birth State" name="birthState" required
                    options={INDIAN_STATES} />
                  <F label="Birth Country" name="birthCountry" placeholder="India" />
                  <S label="Urban / Rural" name="birthUrbanRural" options={["Urban", "Rural"]} />
                </div>
              )}

              {/* ════════════════════════════════════════════════════════════
                TAB 5 — CONTACT & ADDRESS
            ════════════════════════════════════════════════════════════ */}
              {activeTab === "address" && (
                <div className="form-grid">
                  <SH title="Contact" />
                  <F label="Mobile Number" name="mobile" placeholder="10-digit mobile" required hint="Exactly 10 digits" />
                  <F label="Email" name="email" placeholder="student@example.com (optional)" type="email" />

                  <SH title="Current Address" />
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Address <span style={{ color: "var(--danger)" }}>*</span></label>
                    <textarea name="address" value={form.address} onChange={handleChange} rows={2}
                      placeholder="House/Flat No., Street, Area"
                      className={`form-control ${errors.address ? "error" : ""}`} style={{ resize: "vertical" }} />
                    {errors.address && <span className="form-error">{errors.address}</span>}
                  </div>
                  <F label="City" name="city" placeholder="e.g. Nagpur" required />
                  <F label="Taluka" name="taluka" placeholder="e.g. Nagpur" />
                  <F label="District" name="district" placeholder="e.g. Nagpur" />
                  <S label="State" name="state" required options={INDIAN_STATES} />
                  <F label="Pincode" name="pincode" placeholder="6-digit pincode" hint="Optional — exactly 6 digits" />
                  <F label="Country" name="country" placeholder="India" />
                  <S label="Urban / Rural" name="addressUrbanRural" options={["Urban", "Rural"]} />
                </div>
              )}

              {/* ════════════════════════════════════════════════════════════
                TAB 6 — BANK & OTHER
            ════════════════════════════════════════════════════════════ */}
              {activeTab === "bank" && (
                <div className="form-grid">
                  <SH title="Bank Account Details" />
                  <F label="Bank Account No." name="bankAccountNo" placeholder="Account number" />
                  <F label="Bank Name" name="bankName" placeholder="e.g. State Bank of India" />
                  <F label="Branch Name" name="branchName" placeholder="e.g. Nagpur Main Branch" />
                  <F label="IFSC Code" name="ifscCode" placeholder="e.g. SBIN0001234" />
                </div>
              )}

              {/* ── Navigation buttons ─────────────────────────────────── */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1.5rem", paddingTop: "1rem", borderTop: "1px solid var(--border)" }}>
                {/* Prev tab */}
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={TABS[0].id === activeTab}
                  onClick={() => {
                    const idx = TABS.findIndex((t) => t.id === activeTab);
                    if (idx > 0) setActiveTab(TABS[idx - 1].id);
                  }}
                >
                  ← Previous
                </button>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  {/* Next tab */}
                  {activeTab !== TABS[TABS.length - 1].id && (
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        const idx = TABS.findIndex((t) => t.id === activeTab);
                        setActiveTab(TABS[idx + 1].id);
                      }}
                    >
                      Next →
                    </button>
                  )}

                  {/* Save — always visible */}
                  <button type="button" className="btn btn-outline" onClick={() => navigate("/students")}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? "Saving..." : isEdit ? "Update Student" : "Add Student"}
                  </button>
                </div>
              </div>

            </div>
          </div>
        </form>
      </div>
    </StudentFormContext.Provider>
  );
}
